import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Direct Google OAuth sign-in button using Google Identity Services.
 * Verifies the ID token server-side at POST /api/auth/google, which auto-links
 * existing accounts by email and issues the app's HttpOnly JWT cookies.
 */
export function GoogleAuthButton({ redirectTo, testId = "google-signin-btn" }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const routeAfterAuth = (user) => {
    if (redirectTo) return navigate(redirectTo, { replace: true });
    if (user?.enrolled_courses?.length > 0) {
      const latest = [...user.enrolled_courses].sort((a, b) =>
        String(b.enrolled_at || "").localeCompare(String(a.enrolled_at || ""))
      )[0];
      return navigate(`/courses/${latest.course_id}`, { replace: true });
    }
    if (!user?.survey_completed) return navigate("/survey", { replace: true });
    navigate(user.survey_purpose === "Immigration" ? "/immigration" : "/coaching", { replace: true });
  };

  const handleSuccess = async (resp) => {
    const credential = resp?.credential;
    if (!credential) {
      toast.error("Google didn't return a credential. Please try again.");
      return;
    }
    try {
      const { data } = await api.post("/auth/google", { credential });
      setUser(data);
      toast.success(`Welcome, ${data.name}!`);
      routeAfterAuth(data);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed";
      toast.error(detail);
    }
  };

  return (
    <div className="w-full flex justify-center" data-testid={testId}>
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={() => toast.error("Google sign-in was cancelled or failed.")}
        useOneTap={false}
        theme="outline"
        size="large"
        shape="pill"
        text="continue_with"
        width="320"
      />
    </div>
  );
}
